import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ReactElement } from 'react';
import { cn } from '../util/classes.ts';

export const checkboxVariants = cva(
    [
        'appearance-none size-4 shrink-0 cursor-pointer',
        'border border-line rounded-tight bg-container',
        'transition duration-state ease-state',
        'bg-no-repeat bg-center bg-size-[80%_80%]',
        'hover:border-line-strong',
        'focus-visible:outline-1 focus-visible:outline-(--color-main) focus-visible:outline-offset-1',
        'state-disabled:pointer-events-none state-disabled:opacity-disabled',
        // checked: filled with --color-main + white checkmark
        'state-checked:bg-(--color-main) state-checked:border-(--color-main)',
        'state-checked:bg-(image:--checkbox-tick)',
        // indeterminate: same fill + white dash
        'state-indeterminate:bg-(--color-main) state-indeterminate:border-(--color-main)',
        'state-indeterminate:bg-(image:--checkbox-dash)',
    ],
    {
        variants: {
            variant: {
                default: ['[--color-main:var(--color-content)]'],
                primary: ['[--color-main:var(--color-brand)]'],
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

type CheckboxVariantProps = VariantProps<typeof checkboxVariants>;

export type CheckboxVariant = NonNullable<CheckboxVariantProps['variant']>;
export type CheckboxProps = ComponentPropsWithRef<'input'> & CheckboxVariantProps;

export function Checkbox({ className, variant, ...props }: CheckboxProps): ReactElement {
    return (
        <input
            type="checkbox"
            data-slot="checkbox"
            {...props}
            className={cn(checkboxVariants({ variant }), className)}
        />
    );
}
